import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Database, Calendar, Layers, ArrowLeftRight, TrendingUp, Phone, Clock, CheckCircle, PhoneCall, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const SectionComparisonReport = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sectionList, setSectionList] = useState([]);
  const [leftSection, setLeftSection] = useState('');
  const [rightSection, setRightSection] = useState('');
  const [leftMetrics, setLeftMetrics] = useState(null);
  const [rightMetrics, setRightMetrics] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchSectionList(parsedUser.entityId);
      fetchBenchmarks(parsedUser.entityId);
    }
  }, []);

  const fetchBenchmarks = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/industry-benchmarks/${propertyId}`);
      if (response.data.success && response.data.benchmark) {
        setBenchmarks(response.data.benchmark);
      }
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
    }
  };

  const fetchSectionList = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sections/${propertyId}`);
      if (response.data.success) {
        setSectionList(response.data.sections);
      }
    } catch (error) {
      console.error('Error fetching section list:', error);
    }
  };

  const fetchMetrics = async (sectionId, side) => {
    if (!user || !sectionId) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/analytics/section-comparison/${user.entityId}/${sectionId}`,
        { params: { from_date: fromDate, to_date: toDate } }
      );
      
      if (response.data.success) {
        if (side === 'left') {
          setLeftMetrics(response.data.metrics);
        } else {
          setRightMetrics(response.data.metrics);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${side} metrics:`, error);
      toast({
        title: "Error",
        description: `Failed to fetch metrics for ${side} section`,
        variant: "destructive"
      });
    }
  };

  const handleCompare = async () => {
    if (!leftSection || !rightSection) {
      toast({
        title: "Select Sections",
        description: "Please select both sections to compare",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    await Promise.all([
      fetchMetrics(leftSection, 'left'),
      fetchMetrics(rightSection, 'right')
    ]);
    setLoading(false);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatMinutes = (minutes) => {
    if (!minutes) return '0m';
    if (minutes < 60) return `${minutes.toFixed(1)}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const getComparisonColor = (left, right, higherIsBetter = true) => {
    if (left === right) return { left: 'text-slate-700', right: 'text-slate-700' };
    if (higherIsBetter) {
      return {
        left: left > right ? 'text-green-600 font-bold' : 'text-slate-600',
        right: right > left ? 'text-green-600 font-bold' : 'text-slate-600'
      };
    } else {
      return {
        left: left < right ? 'text-green-600 font-bold' : 'text-slate-600',
        right: right < left ? 'text-green-600 font-bold' : 'text-slate-600'
      };
    }
  };

  // Get benchmark indicator for a value
  const getBenchmarkIndicator = (value, benchmarkValue, higherIsBetter) => {
    if (benchmarkValue === null || benchmarkValue === undefined) return null;
    
    if (value > benchmarkValue) {
      if (higherIsBetter === true) {
        return { icon: ArrowUp, color: 'text-green-500', label: 'Above benchmark' };
      } else if (higherIsBetter === false) {
        return { icon: ArrowUp, color: 'text-red-500', label: 'Above benchmark' };
      }
      return { icon: ArrowUp, color: 'text-blue-500', label: 'Above benchmark' };
    } else if (value < benchmarkValue) {
      if (higherIsBetter === true) {
        return { icon: ArrowDown, color: 'text-red-500', label: 'Below benchmark' };
      } else if (higherIsBetter === false) {
        return { icon: ArrowDown, color: 'text-green-500', label: 'Below benchmark' };
      }
      return { icon: ArrowDown, color: 'text-blue-500', label: 'Below benchmark' };
    }
    return { icon: Minus, color: 'text-slate-400', label: 'At benchmark' };
  };

  const getLeftSectionName = () => sectionList.find(s => s.id === leftSection)?.name || 'Section 1';
  const getRightSectionName = () => sectionList.find(s => s.id === rightSection)?.name || 'Section 2';

  const metrics = [
    { 
      key: 'totalAllocations', 
      label: 'Total Allocations', 
      icon: TrendingUp,
      format: (v) => v || 0,
      higherIsBetter: true
    },
    { 
      key: 'avgAllocationLength', 
      label: 'Avg Allocation Length', 
      icon: Clock,
      format: (v) => formatMinutes(v),
      higherIsBetter: null
    },
    { 
      key: 'totalCalls', 
      label: 'Total Calls', 
      icon: Phone,
      format: (v) => v || 0,
      higherIsBetter: true
    },
    { 
      key: 'avgCallsPerAllocation', 
      label: 'Avg Calls Per Allocation', 
      icon: Phone,
      format: (v) => (v || 0).toFixed(2),
      higherIsBetter: true
    },
    { 
      key: 'avgTimePerCall', 
      label: 'Avg Time Per Call', 
      icon: Clock,
      format: (v) => formatTime(v),
      higherIsBetter: false
    },
    { 
      key: 'proactiveCheckins', 
      label: 'Total Proactive Checkins', 
      icon: CheckCircle,
      format: (v) => v || 0,
      higherIsBetter: true
    },
    { 
      key: 'avgProactivePerAllocation', 
      label: 'Avg Proactive Per Allocation', 
      icon: CheckCircle,
      format: (v) => (v || 0).toFixed(2),
      higherIsBetter: true
    },
    { 
      key: 'onDemandCheckins', 
      label: 'Total On Demand Calls', 
      icon: PhoneCall,
      format: (v) => v || 0,
      higherIsBetter: null
    },
    { 
      key: 'avgOnDemandPerAllocation', 
      label: 'Avg On Demand Per Allocation', 
      icon: PhoneCall,
      format: (v) => (v || 0).toFixed(2),
      higherIsBetter: null
    }
  ];

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Section Comparison Report</h1>
            <p className="text-slate-600 mt-1">Compare performance metrics between two sections</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* From Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* To Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Left Section */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-500" />
                Section 1
              </label>
              <Select value={leftSection} onValueChange={setLeftSection}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Section" />
                </SelectTrigger>
                <SelectContent>
                  {sectionList.map((section) => (
                    <SelectItem key={section.id} value={section.id} disabled={section.id === rightSection}>
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Right Section */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4 text-green-500" />
                Section 2
              </label>
              <Select value={rightSection} onValueChange={setRightSection}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Section" />
                </SelectTrigger>
                <SelectContent>
                  {sectionList.map((section) => (
                    <SelectItem key={section.id} value={section.id} disabled={section.id === leftSection}>
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Compare Button */}
            <Button
              onClick={handleCompare}
              disabled={loading || !leftSection || !rightSection}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Comparing...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  Compare
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Comparison Results */}
        {(leftMetrics || rightMetrics) && (
          <>
          <Card className="overflow-hidden">
            {/* Header Row */}
            <div className="grid grid-cols-3 bg-gradient-to-r from-blue-500 via-slate-600 to-green-500">
              <div className="p-4 text-center">
                <h3 className="text-lg font-bold text-white">{getLeftSectionName()}</h3>
                <p className="text-blue-100 text-sm">Section 1</p>
              </div>
              <div className="p-4 text-center bg-slate-700">
                <h3 className="text-lg font-bold text-white">Metrics</h3>
                <p className="text-slate-300 text-sm">Comparison</p>
              </div>
              <div className="p-4 text-center">
                <h3 className="text-lg font-bold text-white">{getRightSectionName()}</h3>
                <p className="text-green-100 text-sm">Section 2</p>
              </div>
            </div>

            {/* Metrics Rows */}
            <div className="divide-y divide-slate-200">
              {metrics.map((metric, index) => {
                const leftValue = leftMetrics?.[metric.key] || 0;
                const rightValue = rightMetrics?.[metric.key] || 0;
                const benchmarkValue = benchmarks?.[metric.key];
                const colors = metric.higherIsBetter !== null 
                  ? getComparisonColor(leftValue, rightValue, metric.higherIsBetter)
                  : { left: 'text-slate-700', right: 'text-slate-700' };
                const Icon = metric.icon;
                const leftBenchmark = getBenchmarkIndicator(leftValue, benchmarkValue, metric.higherIsBetter);
                const rightBenchmark = getBenchmarkIndicator(rightValue, benchmarkValue, metric.higherIsBetter);

                return (
                  <div 
                    key={metric.key} 
                    className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                  >
                    {/* Left Value */}
                    <div className="p-4 text-center border-r border-slate-200">
                      <div className="flex items-center justify-center gap-2">
                        <span className={`text-2xl font-bold ${colors.left}`}>
                          {metric.format(leftValue)}
                        </span>
                        {leftBenchmark && (
                          <leftBenchmark.icon className={`w-4 h-4 ${leftBenchmark.color}`} title={leftBenchmark.label} />
                        )}
                      </div>
                    </div>

                    {/* Metric Name */}
                    <div className="p-4 flex flex-col items-center justify-center gap-1 bg-slate-100 border-r border-slate-200">
                      <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700 text-center">
                          {metric.label}
                        </span>
                      </div>
                      {benchmarkValue !== null && benchmarkValue !== undefined && (
                        <span className="text-xs text-slate-400">
                          Benchmark: {metric.format(benchmarkValue)}
                        </span>
                      )}
                    </div>

                    {/* Right Value */}
                    <div className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className={`text-2xl font-bold ${colors.right}`}>
                          {metric.format(rightValue)}
                        </span>
                        {rightBenchmark && (
                          <rightBenchmark.icon className={`w-4 h-4 ${rightBenchmark.color}`} title={rightBenchmark.label} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Key Metrics Bar Chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Key Metrics Comparison</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Allocations', left: leftMetrics?.totalAllocations || 0, right: rightMetrics?.totalAllocations || 0 },
                    { name: 'Total Calls', left: leftMetrics?.totalCalls || 0, right: rightMetrics?.totalCalls || 0 },
                    { name: 'Proactive', left: leftMetrics?.proactiveCheckins || 0, right: rightMetrics?.proactiveCheckins || 0 },
                    { name: 'On Demand', left: leftMetrics?.onDemandCheckins || 0, right: rightMetrics?.onDemandCheckins || 0 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="left" name={getLeftSectionName()} fill="#3b82f6" />
                  <Bar dataKey="right" name={getRightSectionName()} fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Averages Bar Chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Average Metrics Comparison</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Calls/Alloc', left: leftMetrics?.avgCallsPerAllocation || 0, right: rightMetrics?.avgCallsPerAllocation || 0 },
                    { name: 'Proactive/Alloc', left: leftMetrics?.avgProactivePerAllocation || 0, right: rightMetrics?.avgProactivePerAllocation || 0 },
                    { name: 'OnDemand/Alloc', left: leftMetrics?.avgOnDemandPerAllocation || 0, right: rightMetrics?.avgOnDemandPerAllocation || 0 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="left" name={getLeftSectionName()} fill="#3b82f6" />
                  <Bar dataKey="right" name={getRightSectionName()} fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Time Metrics */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Time Metrics (seconds)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Avg Time/Call', left: leftMetrics?.avgTimePerCall || 0, right: rightMetrics?.avgTimePerCall || 0, benchmark: benchmarks?.avgTimePerCall },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="left" name={getLeftSectionName()} fill="#3b82f6" />
                  <Bar dataKey="right" name={getRightSectionName()} fill="#22c55e" />
                  {benchmarks?.avgTimePerCall && <ReferenceLine y={benchmarks.avgTimePerCall} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Benchmark', position: 'right', fontSize: 10 }} />}
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Proactive vs On Demand Pie Charts */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Call Type Distribution</h3>
              <div className="flex justify-around">
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-2">{getLeftSectionName()}</p>
                  <ResponsiveContainer width={150} height={150}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Proactive', value: leftMetrics?.proactiveCheckins || 0 },
                          { name: 'On Demand', value: leftMetrics?.onDemandCheckins || 0 },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={60}
                        dataKey="value"
                        label={({ percent }) => percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
                      >
                        <Cell fill="#22c55e" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-2">{getRightSectionName()}</p>
                  <ResponsiveContainer width={150} height={150}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Proactive', value: rightMetrics?.proactiveCheckins || 0 },
                          { name: 'On Demand', value: rightMetrics?.onDemandCheckins || 0 },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={60}
                        dataKey="value"
                        label={({ percent }) => percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
                      >
                        <Cell fill="#22c55e" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-slate-600">Proactive</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <span className="text-xs text-slate-600">On Demand</span>
                </div>
              </div>
            </Card>
          </div>
          </>
        )}

        {/* Empty State */}
        {!leftMetrics && !rightMetrics && (
          <Card className="p-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Database className="w-10 h-10 text-teal-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Select Sections to Compare</h2>
              <p className="text-slate-600 max-w-md mx-auto">
                Choose two sections and a date range, then click "Compare" to see the performance comparison.
              </p>
            </div>
          </Card>
        )}
      </div>
    </UserLayout>
  );
};
